import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase-config";
import Nav from "../components/Nav";
import LibraryMap from "../components/LibraryMap";
import CouncilRecommendation from "../components/CouncilRecommendation";
import SearchPanel from "../components/SearchPanel";
import BranchPanel from "../components/BranchPanel";
import OverviewPanel from "../components/OverviewPanel";

function MapPage() {
  const location = useLocation();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [panelView, setPanelView] = useState(location.state?.panel ?? "home"); // "home" | "branch" | "council" | "search"
  const [searchContext, setSearchContext] = useState(null); // { branch, council }
  const [overviewCollapsed, setOverviewCollapsed] = useState(false);

  useEffect(() => {
    getDocs(collection(db, "library_branches"))
      .then((snapshot) => {
        const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setBranches(data);
      })
      .catch((err) => {
        console.error("Error loading branches:", err);
        setError(
          "Could not load library data. Please check your Firebase configuration.",
        );
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && panelView !== "home") {
        setSelectedBranch(null);
        setPanelView("home");
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [panelView]);

  const handleBranchClick = (branch) => {
    setSelectedBranch(branch);
    setPanelView("branch");
  };

  const handleBack = () => {
    setSelectedBranch(null);
    setPanelView("home");
  };

  const handleSearchCollection = () => {
    setSearchContext({
      branch: selectedBranch.branch_name,
      council: selectedBranch.council,
    });
    setPanelView("search");
  };

  return (
    <div className="map-page">
      <Nav
        onMyCouncil={() => {
          setSelectedBranch(null);
          setPanelView("council");
        }}
        onSearch={() => {
          setSelectedBranch(null);
          setSearchContext(null);
          setPanelView("search");
        }}
        searchActive={panelView === "search"}
        councilActive={panelView === "council"}
      />

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner" />
          <p>Loading library branches…</p>
        </div>
      )}

      {error && !loading && (
        <div className="loading-overlay">
          <p
            style={{
              color: "#e74c3c",
              textAlign: "center",
              maxWidth: 360,
              padding: "0 20px",
            }}
          >
            {error}
          </p>
        </div>
      )}

      <div className="map-wrapper">
        <div className={`info-panel${panelView === "home" ? ` info-panel--overview${overviewCollapsed ? " info-panel--collapsed" : ""}` : ""}`}>
          {panelView === "search" ? (
            <>
              <button className="panel-back-btn" onClick={handleBack}>
                ← Back
              </button>
              <SearchPanel
                key={JSON.stringify(searchContext)}
                initialBranch={searchContext?.branch}
                initialCouncil={searchContext?.council}
              />
            </>
          ) : panelView === "branch" && selectedBranch ? (
            <>
              <button className="panel-back-btn" onClick={handleBack}>
                ← Back
              </button>
              <BranchPanel
                branch={selectedBranch}
                onSearchCollection={handleSearchCollection}
              />
            </>
          ) : panelView === "council" ? (
            <>
              <button className="panel-back-btn" onClick={handleBack}>
                ← Back
              </button>
              <CouncilRecommendation inline onClose={handleBack} />
            </>
          ) : (
            <>
              <button
                className="panel-collapse-toggle"
                onClick={() => setOverviewCollapsed((o) => !o)}
                aria-label={overviewCollapsed ? "Expand panel" : "Collapse panel"}
              >
                {overviewCollapsed ? "▾ Overview" : "▴ Hide"}
              </button>
              <OverviewPanel />
            </>
          )}
        </div>
        <div className="map-area">
          <LibraryMap branches={branches} onBranchClick={handleBranchClick} />
        </div>
      </div>
    </div>
  );
}

export default MapPage;
